import type { FieldTree, SchemaPathTree } from '@angular/forms/signals';
import { readonly, validateStandardSchema } from '@angular/forms/signals';

import type { ColumnEntity, DefaultColumn, Device, SpecificColumn, ZeroColumn } from '@sites/entity-core';
import {
    columnDefaultV0Schema,
    columnSpecificV0Schema,
    columnZeroV0Schema,
    compactColumnProps,
} from '@sites/entity-core';

import type { BaseEntityFormOptions } from '../../core/create-base-entity-form';
import { createBaseEntityForm } from '../../core/create-base-entity-form';
import { mergeAccessors } from '../../core/merge-accessors';
import { writeDevice } from '../../core/write-device';
import { applyAnimationFieldRules, createAnimationAccessors } from '../../features/animation/animation-accessors';
import { applyBackgroundFieldRules, createBackgroundAccessors } from '../../features/background/background-accessors';
import { applyBorderFieldRules, createBorderAccessors } from '../../features/border/border-accessors';
import {
    applyBorderRadiusFieldRules,
    createBorderRadiusAccessors,
} from '../../features/border-radius/border-radius-accessors';
import {
    applyScrollEffectFieldRules,
    createScrollEffectAccessors,
} from '../../features/scroll-effect/scroll-effect-accessors';
import { applyShadowFieldRules, createShadowAccessors } from '../../features/shadow/shadow-accessors';
import { withSpacingSides } from '../../mixins/spacing/spacing-updaters';

export type ColumnFormModel = ColumnEntity;
export type ColumnDefaultProps = DefaultColumn['props'];
export type ColumnSpecificProps = SpecificColumn['props'];

export type ColumnSpacingValue = ColumnDefaultProps['padding']['desktop']['value'];
export type ColumnCornersValue = ColumnDefaultProps['borderRadius']['corners']['desktop']['value'];
export type ColumnBorderSide = NonNullable<ColumnDefaultProps['border']['top']>['desktop'];
export type ColumnBackgroundColor = ColumnDefaultProps['background']['color']['desktop'];
export type ColumnBackgroundImage = ColumnDefaultProps['background']['image']['desktop'];

export type ColumnDefaultForm = ReturnType<typeof createColumnDefaultForm>;
export type ColumnSpecificForm = ReturnType<typeof createColumnSpecificForm>;
export type ColumnZeroForm = ReturnType<typeof createColumnZeroForm>;
export type ColumnForm = ColumnDefaultForm | ColumnSpecificForm | ColumnZeroForm;

/** `col` for twelfths of the grid, `%` for a share of the row. */
export type ColumnWidthUnit = ColumnDefaultProps['width']['unit'];

type ColumnVariant = 'default' | 'specific' | 'zero';

/**
 * The slices of a column the two shared accessor groups touch — the envelope all
 * three subtypes have, and the one knob the two styleable ones do.
 *
 * Concrete, not generic. `FieldTree<T>` is a conditional type, so through a
 * bounded generic TypeScript defers it and `_raw.props` stops being navigable;
 * naming the shared shape is what lets the getters below hand back a real leaf
 * type instead of `any`. The narrowing cast at each call site is the price, and
 * it buys a typed accessor for every consumer.
 */
type ColumnEnvelopeLike = Pick<DefaultColumn, 'children'>;
type ColumnWidthLike = { props: Pick<ColumnDefaultProps, 'width'> };

type EnvelopeBase = { _raw: FieldTree<ColumnEnvelopeLike> };
type WidthBase = { _raw: FieldTree<ColumnWidthLike> };

/**
 * `id`, `parentId` and the version tags are infrastructure: the editor never
 * lets the user mutate them, and the host's move/copy actions own the parent
 * link, so they are read-only on the schema path.
 */
/** Same story as the accessors: the schema path is a conditional type too. */
const configureColumnReadonly = (schemaPath: SchemaPathTree<ColumnEntity>): void => {
    readonly(schemaPath.id);
    readonly(schemaPath.parentId);
    readonly(schemaPath.entityType);
    readonly(schemaPath.entitySubType);
    readonly(schemaPath.version);
};

/**
 * Envelope accessors all three subtypes share.
 *
 * `parentId` is deliberately absent: the base form already exposes it as a
 * read-only signal, it is `readonly` on the schema path, and re-declaring it
 * here as a FieldTree gave the merged form two `parentId`s with two different
 * call results — the grid and tab forms, which never re-declared it, read a
 * plain string from the same name. One name, one meaning.
 */
const createCommonAccessors = (base: EnvelopeBase) => {
    const { _raw } = base;

    return {
        /** Child ids — the host owns adds/removes; the form only reorders. */
        get children() {
            return _raw.children;
        },
        setChildren: (value: { id: string; schema: string }[]) => {
            _raw.children().value.set(value);
        },
        updateChildren: (updater: (current: unknown[]) => unknown[]) => {
            _raw.children().value.update(updater);
        },
    };
};

/**
 * How wide the column sits in its row — the one knob every styleable subtype has.
 * The unit travels with the number, so a resize writes the value alone and only
 * a change of the row's mode touches both.
 */
const createWidthAccessors = (base: WidthBase) => {
    const { _raw } = base;

    return {
        get width() {
            return _raw.props.width;
        },
        setWidth: (value: number) => {
            _raw.props.width.value().value.set(value);
        },
        setWidthWithUnit: (value: number, unit: ColumnWidthUnit) => {
            _raw.props.width().value.set({ value, unit });
        },
    };
};

export function createColumnDefaultForm(options?: BaseEntityFormOptions<DefaultColumn>) {
    const base = createBaseEntityForm<DefaultColumn>((schemaPath) => {
        configureColumnReadonly(schemaPath);

        // Every styling group is a self-versioned feature, and its `version` is
        // stamped by the form the way the entity's own is — never by a panel.
        applyBorderFieldRules(schemaPath.props.border);
        applyBackgroundFieldRules(schemaPath.props.background);
        applyBorderRadiusFieldRules(schemaPath.props.borderRadius);
        applyShadowFieldRules(schemaPath.props.shadow);
        applyScrollEffectFieldRules(schemaPath.props.scrollEffect);
        applyAnimationFieldRules(schemaPath.props.animation);

        validateStandardSchema(schemaPath, columnDefaultV0Schema);
    }, options);

    const { _raw } = base;

    // `mergeAccessors`, not object spread: spreading would call the getters while
    // the model is still the empty seed.
    //
    // The four styling groups are features, so their accessors come from the
    // feature rather than being written out again here — the same way the
    // animation ones already do. Thunks throughout: the tree paths only exist
    // once the entity has been synced.
    const withFeatures = mergeAccessors(
        mergeAccessors(
            mergeAccessors(
                mergeAccessors(
                    mergeAccessors(
                        mergeAccessors({ ...base }, createCommonAccessors(base as EnvelopeBase)),
                        createWidthAccessors(base as WidthBase)
                    ),
                    createBorderAccessors(() => _raw.props.border)
                ),
                createBorderRadiusAccessors(() => _raw.props.borderRadius)
            ),
            createShadowAccessors(() => _raw.props.shadow)
        ),
        createScrollEffectAccessors(() => _raw.props.scrollEffect)
    );

    const withAnimation = mergeAccessors(
        mergeAccessors(
            withFeatures,
            createBackgroundAccessors(() => _raw.props.background)
        ),
        createAnimationAccessors(() => _raw.props.animation)
    );

    return mergeAccessors(withAnimation, {
        /** Stores only what says something — see `compactColumnProps` and the block form's twin. */
        compact: () => {
            _raw.props().value.update((props: ColumnDefaultProps) => compactColumnProps(props));
        },

        /** Write desktop style edits through to the tablet and mobile tabs. */
        get syncWithDesktop() {
            return _raw.props.syncWithDesktop;
        },
        setSyncWithDesktop: (value: boolean) => {
            _raw.props.syncWithDesktop().value.set(value);
        },

        get verticalAlign() {
            return _raw.props.verticalAlign;
        },
        setVerticalAlign: (value: ColumnDefaultProps['verticalAlign']) => {
            _raw.props.verticalAlign().value.set(value);
        },
        get horizontalAlign() {
            return _raw.props.horizontalAlign;
        },
        setHorizontalAlign: (value: ColumnDefaultProps['horizontalAlign']) => {
            _raw.props.horizontalAlign().value.set(value);
        },

        // --- Responsive leaves ----------------------------------------------
        // Each takes the breakpoint it writes, so the panel's three tabs share
        // one setter instead of one per device.

        get padding() {
            return _raw.props.padding;
        },
        /** The four sides live in one leaf, so a single-side edit merges into it. */
        setPadding: (patch: Partial<ColumnSpacingValue>, device: Device = 'desktop') => {
            writeDevice(_raw.props.padding, device, withSpacingSides(patch));
        },
    });
}

/**
 * The header slot. Its alignment comes from the position it was created in and
 * the settings panel is unreachable for it, so the form only carries what the
 * host writes programmatically.
 */
export function createColumnSpecificForm(options?: BaseEntityFormOptions<SpecificColumn>) {
    const base = createBaseEntityForm<SpecificColumn>((schemaPath) => {
        configureColumnReadonly(schemaPath);
        validateStandardSchema(schemaPath, columnSpecificV0Schema);
    }, options);

    const { _raw } = base;

    return mergeAccessors(
        mergeAccessors(
            mergeAccessors({ ...base }, createCommonAccessors(base as EnvelopeBase)),
            createWidthAccessors(base as WidthBase)
        ),
        {
            get horizontalAlign() {
                return _raw.props.horizontalAlign;
            },
            setHorizontalAlign: (value: ColumnSpecificProps['horizontalAlign']) => {
                _raw.props.horizontalAlign().value.set(value);
            },
        }
    );
}

export function createColumnZeroForm(options?: BaseEntityFormOptions<ZeroColumn>) {
    const base = createBaseEntityForm<ZeroColumn>((schemaPath) => {
        configureColumnReadonly(schemaPath);
        validateStandardSchema(schemaPath, columnZeroV0Schema);
    }, options);

    // A zero column carries no settings of its own — only the envelope.
    return mergeAccessors({ ...base }, createCommonAccessors(base as EnvelopeBase));
}

/**
 * Picks the factory from the entity's own subtype. The three variants differ in
 * shape, so the schema has to be chosen at construction — a header column handed
 * to the default factory would validate against a schema expecting a background
 * and a contour it never had.
 */
export function createColumnForm(options?: BaseEntityFormOptions<ColumnEntity>): ColumnForm {
    const entity =
        (options as { initialData?: ColumnEntity })?.initialData ?? (options?.entity?.() as ColumnEntity | undefined);
    const variant: ColumnVariant = entity?.entitySubType ?? 'default';

    if (variant === 'specific') {
        return createColumnSpecificForm(options as BaseEntityFormOptions<SpecificColumn>);
    }

    if (variant === 'zero') {
        return createColumnZeroForm(options as BaseEntityFormOptions<ZeroColumn>);
    }

    return createColumnDefaultForm(options as BaseEntityFormOptions<DefaultColumn>);
}
